import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import { INTL_SHORT_TIME_FORMAT_AM_PM, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getDateTimeLabel } from 'lib/airportTransfer/airportTransferUtils'
import { fillArray } from 'lib/array/arrayUtils'
import { convertTZ, getBrowserTimeZone, getCalendarDateStringInTimeZone, intlDateFormatter } from 'lib/datetime/dateUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'

const minutesInDay = 24 * 60
const TIMES_STEP = 30

function getCurrentTimeForSelectedDate(
  selectedDate: moment.Moment | undefined,
  now: Date | undefined,
  timeZone?: string,
): string | undefined {
  if (!selectedDate || !now) {
    return undefined
  }

  try {
    const resolvedTimeZone = timeZone ?? getBrowserTimeZone()
    if (selectedDate.format(ISO_DATE_FORMAT) !== getCalendarDateStringInTimeZone(now, resolvedTimeZone)) {
      return undefined
    }

    return moment(convertTZ(now, resolvedTimeZone)).format('HH:mm')
  } catch {
    return undefined
  }
}

function getLaterTime(firstTime?: string, secondTime?: string): string | undefined {
  if (!firstTime) return secondTime
  if (!secondTime) return firstTime

  return firstTime > secondTime ? firstTime : secondTime
}

function generateTimes(intl: IntlShape, step = TIMES_STEP, min?: string, max?: string) {
  const count = minutesInDay / step
  const stepsInHour = Math.round(60 / step)

  const minMoment = min ? moment(min, 'HH:mm') : undefined
  const maxMoment = max ? moment(max, 'HH:mm') : undefined

  return fillArray(count).map((index) => {
    const hourPart = Math.floor(index / stepsInHour).toString().padStart(2, '0')
    const minutePart = ((index % stepsInHour) * step).toString().padStart(2, '0')
    const value = `${hourPart}:${minutePart}`
    const momentValue = moment(value, 'HH:mm')

    const isDisabledByMin = !!minMoment && moment(momentValue).isBefore(minMoment, 'minutes')
    const isDisabledByMax = !!maxMoment && moment(momentValue).isAfter(maxMoment, 'minutes')

    return {
      value,
      label: intlDateFormatter(momentValue, INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale),
      disabled: isDisabledByMin || isDisabledByMax,
    }
  })
}

function getTimeOptionsForDate(
  intl: IntlShape,
  selectedDate: moment.Moment | undefined,
  minDate: moment.Moment | undefined,
  maxDate: moment.Moment | undefined,
  minTime: string | undefined,
  maxTime: string | undefined,
  now: Date | undefined,
) {
  const currentTime = getCurrentTimeForSelectedDate(selectedDate, now)
  const returnMinTime = minTime && moment(selectedDate).isSame(minDate, 'day') ? minTime : undefined
  const finalMinTime = getLaterTime(currentTime, returnMinTime)
  const finalMaxTime = maxTime && moment(selectedDate).isSame(maxDate, 'day') ? maxTime : undefined

  const timeOptions = generateTimes(intl, TIMES_STEP, finalMinTime, finalMaxTime)

  return currentTime ? timeOptions.filter((option) => option.value >= currentTime) : timeOptions
}

const DayCell = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${rem(50)};
  height: ${rem(56)};
  padding: ${rem(8)} 0;
  border-radius: ${props => props.theme.borderRadius.L};

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.isPickUp {
    justify-content: center;
    padding: 0;
    border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground}
  }

  &:disabled {
    opacity: 0.4;
  }
`

interface Props {
  selectedDate?: moment.Moment
  selectedTime?: string
  pickUpDate?: moment.Moment
  minDate?: moment.Moment
  maxDate?: moment.Moment
  minTime?: string
  maxTime?: string
}

function useAirportTransferDateTimePickerControls(props: Props) {
  const { selectedDate, selectedTime, pickUpDate, minDate, maxDate, minTime, maxTime } = props

  const intl = useIntl()

  const [isTimePickerOpen, setTimePickerOpen] = useState(false)
  const timePickerBodyRef = useRef<HTMLDivElement>(null)
  const [now, setNow] = useState<Date>()

  useEffect(() => {
    setNow(new Date())
  }, [isTimePickerOpen])

  const onOpenTimePicker = useCallback(() => { setTimePickerOpen(true) }, [])
  const onCloseTimePicker = useCallback(() => { setTimePickerOpen(false) }, [])

  const timeOptions = useMemo(
    () => getTimeOptionsForDate(intl, selectedDate, minDate, maxDate, minTime, maxTime, now),
    [intl, maxDate, maxTime, minDate, minTime, now, selectedDate],
  )

  useEffect(() => {
    if (!isTimePickerOpen || !selectedTime || !timePickerBodyRef.current) return

    const selectedTimeElement = timePickerBodyRef.current.querySelector<HTMLButtonElement>(`[data-value='${selectedTime}']`)
    if (!selectedTimeElement) return

    timePickerBodyRef.current.scrollTo({
      top: Math.max(0, selectedTimeElement.offsetTop - (timePickerBodyRef.current.offsetHeight / 2)),
      behavior: 'auto',
    })
  }, [isTimePickerOpen, selectedTime])

  const getAvailableTimeForDate = useCallback((date: moment.Moment, time: string) => {
    const optionsForDate = getTimeOptionsForDate(intl, date, minDate, maxDate, minTime, maxTime, now)

    return optionsForDate.find((option) => option.value === time && !option.disabled)?.value ??
      optionsForDate.find((option) => !option.disabled)?.value ??
      time
  }, [intl, maxDate, maxTime, minDate, minTime, now])

  const dayCellRender = useCallback((day: Date, params: { disabled: boolean, selected: boolean, onDayClick?: (day: Date) => void }) => {
    const isPickUp = day.getTime() === pickUpDate?.toDate().getTime()

    return <DayCell
      key={day.toISOString()}
      disabled={params.disabled}
      className={clsx({ selected: params.selected, isPickUp })}
      onClick={() => params.onDayClick?.(day)}
    >
      <BodyText variant="large">{day.getDate()}</BodyText>
      {isPickUp && <Caption variant="small">
        {intl.formatMessage({
          id: 'P3N_k0MRvOPs-WshoLr2',
          defaultMessage: 'Pickup',
          description: 'Label for Pickup day cell in Airport Transfer date picker',
        })}
      </Caption>}
    </DayCell>
  }, [pickUpDate, intl])

  const timeLabel = useMemo(() => {
    return intlDateFormatter(moment(selectedTime, 'HH:mm'), INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)
  }, [selectedTime, intl])

  const dateAndTimeLabel = useMemo(() => {
    return getDateTimeLabel(selectedDate, selectedTime, intl)
  }, [selectedDate, selectedTime, intl])

  const min = useMemo(() => minDate?.toDate() ?? moment().subtract(1, 'day').toDate(), [minDate])
  const max = useMemo(() => maxDate?.toDate() || moment(min).add(3, 'years').toDate(), [maxDate, min])

  return {
    isTimePickerOpen,
    dateAndTimeLabel,
    timeLabel,
    timeOptions,
    timePickerBodyRef,
    getAvailableTimeForDate,
    min,
    max,
    dayCellRender,
    onOpenTimePicker,
    onCloseTimePicker,
  }
}

export default useAirportTransferDateTimePickerControls
